import type { LabResultRecord } from '../../types/labs';
import type { BiomarkerDirection, BiomarkerMeta, BiomarkerTier } from '../../data/biomarkerCatalogue';
import { ALL_BIOMARKERS } from '../../data/biomarkerCatalogue';
import { buildBiomarkerHistory, type BiomarkerHistoryPoint } from '../biomarkerHistory';
import { formatDateLabel } from '../dateLabels';

/**
 * "What's changed" — per-biomarker movement between two readings.
 *
 * Built on `buildBiomarkerHistory`, which already resolves each record against
 * the catalogue, converts to canonical units and drops readings it cannot place
 * on a range. This module is pure arithmetic over that output.
 *
 * ON WORDING: `movement` is 'toward_range' / 'away_from_range' / 'stable', not
 * 'improved' / 'worsened'. Whether a single marker moving in isolation is an
 * improvement is a clinical judgement, and every explanation string in the
 * catalogue is still `copyStatus: 'draft'` pending clinician sign-off
 * (docs/clinical/BIOMARKER_COPY_SIGNOFF.md). The number, the arrow and the
 * direction of travel are descriptive and safe to render today; the verb is
 * not, and is deliberately absent.
 */

export type BiomarkerMovement = 'toward_range' | 'away_from_range' | 'stable';

export interface BiomarkerDelta {
  key: string;
  label: string;
  unit: string;
  tier: BiomarkerTier;
  direction: BiomarkerDirection;
  from: number;
  to: number;
  absChange: number;
  /** Signed percentage change from `from` to `to`. Null when `from` is zero. */
  pctChange: number | null;
  fromAt: Date | null;
  toAt: Date | null;
  fromLabel: string;
  toLabel: string;
  arrow: '↑' | '↓' | '→';
  movement: BiomarkerMovement;
}

export interface BiomarkerDeltaOptions {
  /** The review anchor. Readings at or before it are candidate baselines. */
  since?: Date | null;
  /** Below this, movement is reported as 'stable' rather than as a change. */
  minPctChange?: number;
  /** Two readings closer together than this are treated as the same draw. */
  minDaysApart?: number;
  limit?: number;
}

const DEFAULTS = { minPctChange: 10, minDaysApart: 28, limit: 6 };
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const TIER_RANK: Record<BiomarkerTier, number> = {
  CORE: 0,
  BASELINE: 1,
  CONTEXT: 2,
  OPTIONAL: 3,
};

/**
 * Which of the earlier readings to compare the latest against.
 *
 * Prefer the last reading at or before the review anchor — that is literally
 * "where you were at your last review". When every reading postdates the anchor
 * (a member whose results all arrived this quarter), fall back to the oldest
 * reading that is far enough from the latest to be a different draw.
 */
function pickBaseline(
  points: BiomarkerHistoryPoint[],
  latest: BiomarkerHistoryPoint,
  since: Date | null,
  minDaysApart: number,
): BiomarkerHistoryPoint | null {
  const earlier = points.filter(
    (point) =>
      point !== latest &&
      point.timestamp > 0 &&
      latest.timestamp - point.timestamp >= minDaysApart * MS_PER_DAY,
  );
  if (earlier.length === 0) return null;

  if (since) {
    const atOrBeforeAnchor = earlier.filter((point) => point.timestamp <= since.getTime());
    if (atOrBeforeAnchor.length > 0) {
      // The newest reading that still predates the review.
      return atOrBeforeAnchor[atOrBeforeAnchor.length - 1];
    }
  }

  return earlier[0];
}

function resolveMovement(
  from: number,
  to: number,
  direction: BiomarkerDirection,
  pctChange: number | null,
  minPctChange: number,
): BiomarkerMovement {
  if (pctChange === null || Math.abs(pctChange) < minPctChange) return 'stable';
  const rising = to > from;

  switch (direction) {
    case 'higher_worse':
      return rising ? 'away_from_range' : 'toward_range';
    case 'lower_worse':
      return rising ? 'toward_range' : 'away_from_range';
    default:
      // Two-sided markers (e.g. sodium, TSH) are worse in either direction, so
      // movement alone says nothing about which way is better. Report the change
      // without claiming a direction of travel.
      return 'stable';
  }
}

export function buildBiomarkerDeltas(
  records: LabResultRecord[],
  sex: string | null,
  options: BiomarkerDeltaOptions = {},
): BiomarkerDelta[] {
  const minPctChange = options.minPctChange ?? DEFAULTS.minPctChange;
  const minDaysApart = options.minDaysApart ?? DEFAULTS.minDaysApart;
  const limit = options.limit ?? DEFAULTS.limit;
  const since = options.since ?? null;

  const deltas: BiomarkerDelta[] = [];

  for (const meta of ALL_BIOMARKERS as BiomarkerMeta[]) {
    const points = buildBiomarkerHistory(records, meta, sex);
    if (points.length < 2) continue;

    const latest = points[points.length - 1];
    const baseline = pickBaseline(points, latest, since, minDaysApart);
    if (!baseline) continue;

    const from = baseline.value;
    const to = latest.value;
    const absChange = to - from;
    const pctChange = from === 0 ? null : (absChange / Math.abs(from)) * 100;
    const movement = resolveMovement(from, to, meta.direction, pctChange, minPctChange);

    deltas.push({
      key: meta.key,
      label: meta.label,
      unit: meta.unit,
      tier: meta.tier,
      direction: meta.direction,
      from,
      to,
      absChange,
      pctChange,
      fromAt: baseline.observedAt ? new Date(baseline.observedAt) : null,
      toAt: latest.observedAt ? new Date(latest.observedAt) : null,
      fromLabel: formatDateLabel(baseline.observedAt),
      toLabel: formatDateLabel(latest.observedAt),
      arrow: absChange === 0 ? '→' : absChange > 0 ? '↑' : '↓',
      movement,
    });
  }

  return deltas
    .sort((left, right) => {
      // Clinically weightier markers first, then whichever moved most.
      const tierGap = TIER_RANK[left.tier] - TIER_RANK[right.tier];
      if (tierGap !== 0) return tierGap;
      return Math.abs(right.pctChange ?? 0) - Math.abs(left.pctChange ?? 0);
    })
    .slice(0, limit);
}
